import {
  useDeleteCluster,
  useResyncHive,
} from '@/generated/http-clients/v3ApiComponents'
import {
  ERROR_TOAST_OPTION,
  LOADING_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import get from 'lodash/get'
import { type PropsWithChildren, createContext, useContext } from 'react'

type ClusterContextType = {
  deleteOrgCluster: (orgId: string, clusterId: string) => void
  triggerClusterResync: (orgId: string, clusterId: string) => void
  isResyncing: boolean
}

const ClusterContext = createContext<ClusterContextType>(
  {} as ClusterContextType,
)

export function useCluster() {
  const context = useContext(ClusterContext)
  if (!context) {
    throw new Error('useCluster must be used within an ClusterProvider')
  }
  return context
}

export function ClusterProvider({ children }: Readonly<PropsWithChildren>) {
  const deleteRequest = useDeleteCluster({
    onMutate: () => {
      const toastId = 'delete-cluster-toast'
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: toastId,
        title: 'Deleting cluster...',
      })
      return toastId // Return the toastId for subsequent stages
    },
    onSuccess: (_data: unknown, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: 'Cluster deletion request sent',
        description:
          'This cluster is being deleted. It may take a few minutes to complete.',
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: 'Could not delete cluster',
        description:
          get(error, 'stack.description') ||
          get(error, 'stack._embedded.errors[0].message') ||
          get(error, 'message') ||
          'Something went wrong while deleting the cluster.',
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  function deleteOrgCluster(orgId: string, clusterId: string) {
    deleteRequest.mutateAsync({
      pathParams: {
        orgId,
        clusterId,
      },
    })
  }

  const triggerClusterResyncRequest = useResyncHive({
    onMutate: () => {
      const toastId = 'trigger-cluster-resync-toast'
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: toastId,
        title: 'Triggering cluster resync...',
      })
      return toastId // Return the toastId for subsequent stages
    },
    onSuccess: (_data: unknown, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: 'Cluster resync triggered',
        description:
          'The cluster is being resynced. It may take 30-60 seconds to complete.',
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: 'Could not trigger cluster resync',
        description:
          get(error, 'stack.description') ||
          get(error, 'stack._embedded.errors[0].message') ||
          get(error, 'message') ||
          'Something went wrong while triggering the cluster resync.',
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  function triggerClusterResync(orgId: string, clusterId: string) {
    triggerClusterResyncRequest.mutateAsync({
      pathParams: {
        orgId,
        clusterId,
      },
    })
  }

  return (
    <ClusterContext.Provider
      value={{
        deleteOrgCluster,
        triggerClusterResync,
        isResyncing: triggerClusterResyncRequest.isPending,
      }}
    >
      {children}
    </ClusterContext.Provider>
  )
}
